import React, { memo } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Divider,
  Typography
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BadgeIcon from "@mui/icons-material/Badge";
import WorkIcon from "@mui/icons-material/Work";
import QrCodeIcon from '@mui/icons-material/QrCode';
import { Link } from "react-router-dom";
import { User } from "../../../interfaces/entities/user";
import {
  EmployeeDetailsStyles,
  ProfileSection,
  AvatarWrapper,
  EmployeeNameRow,
  EmployeeName,
  IconWrapper,
  InfoLabel
} from "./employeeDetails.styles";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";

interface EmployeeDetailsProps {
  userData: User;
  onQrCodeClick?: (user: User) => void;
  onDeleteClick?: (user: User) => void;
  isEmployeeOwner?: boolean;
}

// Memoized presentational component for better performance
// Prevents unnecessary re-renders when parent component updates
export const EmployeeDetailsPresentational: React.FC<EmployeeDetailsProps> = memo(({ userData, onQrCodeClick, onDeleteClick, isEmployeeOwner }) => {

  const { currentUserIsOwnerOf } = useAuthenticatedUser();
  const canEditOrDelete = currentUserIsOwnerOf(userData.companyId);

  const handleQrCodeClick = () => {
    if (onQrCodeClick) {
      onQrCodeClick(userData);
    }
  };

  const handleDeleteClick = () => {
    if (onDeleteClick) {
      onDeleteClick(userData);
    }
  };

  return (
    currentUserIsOwnerOf(userData.companyId) ?
      <EmployeeDetailsStyles>
        <ProfileSection>
          <AvatarWrapper
            src={userData.image || "/assets/default-user.jpg"}
            alt={`${userData.firstName} ${userData.lastName}`}
          />
          <Box>
            <EmployeeNameRow>
              <EmployeeName variant="h5">
                {userData.firstName} {userData.lastName}
              </EmployeeName>

              {canEditOrDelete && (
                <Tooltip title="Edit Employee">
                  <IconButton
                    component={Link}
                    to={`edit`}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              {canEditOrDelete && (
                <Tooltip title="Delete Employee">
                  <IconButton onClick={handleDeleteClick} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Generate QR">
                <IconButton onClick={handleQrCodeClick}>
                  <QrCodeIcon />
                </IconButton>
              </Tooltip>
            </EmployeeNameRow>
          </Box>
        </ProfileSection>

        <Divider />

        <Box>
          <IconWrapper>
            <EmailIcon color="action" />
            <Typography>
              <InfoLabel>Email:</InfoLabel> {userData.email || "Not available"}
            </Typography>
          </IconWrapper>

          <IconWrapper>
            <PhoneIcon color="action" />
            <Typography>
              <InfoLabel>Phone:</InfoLabel> {userData.contact || "Not available"}
            </Typography>
          </IconWrapper>

          <IconWrapper>
            <LocationOnIcon color="action" />
            <Typography>
              <InfoLabel>Address:</InfoLabel> {userData.address || "Not available"}
            </Typography>
          </IconWrapper>

          <IconWrapper>
            <BadgeIcon color="action" />
            <Typography>
              <InfoLabel>NIC:</InfoLabel> {userData.nic || "Not available"}
            </Typography>
          </IconWrapper>

          <IconWrapper>
            <WorkIcon color="action" />
            <Typography>
              <InfoLabel>Employee No:</InfoLabel> {userData.empNo || "Not available"}
            </Typography>
          </IconWrapper>
        </Box>

      </EmployeeDetailsStyles> : <></>
  );
});

